// Core add-to-homescreen library functionality
// Integrated directly from add-to-homescreen library source

export enum DeviceType {
  IOS = "IOS",
  ANDROID = "ANDROID",
  DESKTOP = "DESKTOP",
}

export class DeviceInfo {
  isStandAlone: boolean;
  canBeStandAlone: boolean;
  device: DeviceType;
  
  constructor(
    isStandAlone: boolean,
    canBeStandAlone: boolean,
    device: DeviceType
  ) {
    this.isStandAlone = isStandAlone;
    this.canBeStandAlone = canBeStandAlone;
    this.device = device;
  }
}

export interface DisplayOptions {
  showMobile: boolean;
  showDesktop: boolean;
}

export const DISPLAY_OPTIONS_DEFAULT: DisplayOptions = {
  showMobile: true,
  showDesktop: true
}

export function isDisplayOptions(obj: any): obj is DisplayOptions {
  return obj
    && typeof obj.showMobile === 'boolean'
    && typeof obj.showDesktop === 'boolean';
}

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export interface AddToHomeScreenOptions {
  appName: string;
  appIconUrl: string;
  assetUrl: string;
  maxModalDisplayCount: number;
  appNameDisplay?: "standalone" | "inline";
  displayOptions: DisplayOptions;
  allowClose?: boolean;
  showArrow?: boolean;
}

// Supported locales from the library
export const LOCALES = [
  "ar", "da", "de", "en", "es", "fr", "he", "it", "ja", "pt", "ru", 
  "cs", "ko", "lv", "nl", "pl", "sl", "sq", "sr", "sv", "th", "tr", 
  "vn", "zh", "zh_CN", "zh_HK", "zh_TW"
];

// Simple i18n functionality based on library
class SimpleI18n {
  private locale: string = 'en';
  private staticCatalog: { [locale: string]: any } = {};
  
  configure(config: { locales: string[], staticCatalog: { [locale: string]: any }, directory: string }) {
    this.staticCatalog = config.staticCatalog;
  }
  
  setLocale(locale: string) {
    this.locale = locale;
  }
  
  __(key: string): string {
    return this.staticCatalog[this.locale]?.[key] || key;
  }
  
  _getLanguageFromBrowserSettings(): string | null {
    const language = navigator.language || (navigator as any).userLanguage;
    if (!language) return null;
    
    // Try exact match first
    if (LOCALES.includes(language)) return language;
    
    // Try language code only (e.g., "en" from "en-US")
    const langCode = language.split('-')[0];
    if (LOCALES.includes(langCode)) return langCode;
    
    return null;
  }
}

const i18n = new SimpleI18n();

export interface AddToHomeScreenType {
  appName: string;
  appIconUrl: string;
  assetUrl: string;
  maxModalDisplayCount: number;
  displayOptions: DisplayOptions;
  allowClose: boolean;
  showArrow: boolean;

  clearModalDisplayCount: () => void;
  isStandAlone: () => boolean;
  show: (locale?: string) => DeviceInfo;
  showModal: (locale?: string) => DeviceInfo;
  closeModal: () => void;
  modalIsShowing: () => boolean;
  hasReachedMaxModalDisplayCount: () => boolean;
  getCachedInstallPrompt: () => BeforeInstallPromptEvent | null;
  triggerInstallPrompt: () => Promise<void>;
  isDeviceIOS: () => boolean;
  isDeviceAndroid: () => boolean;
  isBrowserIOSSafari: () => boolean;
  isBrowserIOSChrome: () => boolean;
  isBrowserIOSFirefox: () => boolean;
  isBrowserIOSInAppFacebook: () => boolean;
  isBrowserIOSInAppLinkedin: () => boolean;
  isBrowserIOSInAppInstagram: () => boolean;
  isBrowserIOSInAppThreads: () => boolean;
  isBrowserIOSInAppTwitter: () => boolean;
  isBrowserAndroidChrome: () => boolean;
  isBrowserAndroidFacebook: () => boolean;
  isBrowserAndroidSamsung: () => boolean;
  isBrowserAndroidFirefox: () => boolean;
  isDesktopWindows: () => boolean;
  isDesktopMac: () => boolean;
  isDesktopChrome: () => boolean;
  isDesktopSafari: () => boolean;
  isDesktopEdge: () => boolean;
}

// Load locale catalogs dynamically
const localeCatalog: { [locale: string]: any } = {};

const loadLocaleCatalog = async (targetLocale: string = 'en') => {
  try {
    const response = await fetch(`/libs/add-to-homescreen/locales/${targetLocale}.json`);
    if (response.ok) {
      localeCatalog[targetLocale] = await response.json();
      return localeCatalog[targetLocale];
    } else {
      // Fallback to English if target locale fails
      const enResponse = await fetch('/libs/add-to-homescreen/locales/en.json');
      if (enResponse.ok) {
        const enCatalog = await enResponse.json();
        localeCatalog['en'] = enCatalog;
        return enCatalog;
      }
    }
  } catch (e) {
    console.warn('Failed to load locale files, using fallbacks');
  }
  
  // Ultimate fallback
  const fallback = {
    "Add to Home Screen": "Add to Home Screen",
    "An icon will be added to your home screen so you can quickly access this website.": "An icon will be added to your home screen so you can quickly access this website.",
    "An icon will be added to your Taskbar so you can quickly access this website.": "An icon will be added to your Taskbar so you can quickly access this website.",
    "An icon will be added to your Dock so you can quickly access this website.": "An icon will be added to your Dock so you can quickly access this website.",
    "Install": "Install",
    "Later": "Later",
    "Tap %s": "Tap %s",
    "Select %s from the menu that pops up.": "Select %s from the menu that pops up.",
    "Tap the %s button in the toolbar.": "Tap the %s button in the toolbar.",
    "Tap the %s button in the upper right corner.": "Tap the %s button in the upper right corner."
  };
  localeCatalog['en'] = fallback;
  return fallback;
};

export function AddToHomeScreen(options: AddToHomeScreenOptions): AddToHomeScreenType {
  const { appIconUrl, appName, appNameDisplay, assetUrl, maxModalDisplayCount, displayOptions, allowClose, showArrow } = options;
  
  const userAgent = window.navigator.userAgent;
  let modalIsShowingState = false;
  let desktopInstallPromptEvent: BeforeInstallPromptEvent | null = null;
  let closeEventListener: EventListener | null = null;

  // Initialize locale catalog - load default English fallback
  if (!localeCatalog['en']) {
    localeCatalog['en'] = {
      "Add to Home Screen": "Add to Home Screen",
      "An icon will be added to your home screen so you can quickly access this website.": "An icon will be added to your home screen so you can quickly access this website.",
      "An icon will be added to your Taskbar so you can quickly access this website.": "An icon will be added to your Taskbar so you can quickly access this website.",
      "An icon will be added to your Dock so you can quickly access this website.": "An icon will be added to your Dock so you can quickly access this website.",
      "Install": "Install",
      "Later": "Later",
      "Tap %s": "Tap %s",
      "Select %s from the menu that pops up.": "Select %s from the menu that pops up.",
      "Tap the %s button in the toolbar.": "Tap the %s button in the toolbar.",
      "Tap the %s button in the upper right corner.": "Tap the %s button in the upper right corner."
    };
  }

  i18n.configure({
    locales: LOCALES,
    staticCatalog: localeCatalog,
    directory: "."
  });

  /*** Device Detection Functions ***/
  
  function _matchesUserAgent(regex: RegExp): boolean {
    return !!userAgent.match(regex);
  }

  function isDeviceAndroid(): boolean {
    return !!_matchesUserAgent(/Android/);
  }

  function isDeviceIOS(): boolean {
    return _matchesUserAgent(/iPhone|iPad|iPod/) || isBrowserIOSIPadSafari();
  }

  function isBrowserIOSIPadSafari(): boolean {
    return !!(
      _matchesUserAgent(/iPad/) || 
      (
        _matchesUserAgent(/Macintosh/) &&
        navigator.maxTouchPoints &&
        navigator.maxTouchPoints > 1
      )
    );
  }

  function isBrowserIOSSafari(): boolean {
    return (
      isDeviceIOS() &&
      _matchesUserAgent(/Safari/) &&
      !isBrowserIOSChrome() &&
      !isBrowserIOSFirefox() &&
      !isBrowserIOSInAppFacebook() &&
      !isBrowserIOSInAppLinkedin() &&
      !isBrowserIOSInAppInstagram() &&
      !isBrowserIOSInAppThreads() &&
      !isBrowserIOSInAppTwitter()
    );
  }

  function isBrowserIOSChrome(): boolean {
    return isDeviceIOS() && _matchesUserAgent(/CriOS/);
  }

  function isBrowserIOSFirefox(): boolean {
    return isDeviceIOS() && _matchesUserAgent(/FxiOS/);
  }

  function isBrowserIOSInAppFacebook(): boolean {
    return isDeviceIOS() && _matchesUserAgent(/FBAN|FBAV/);
  }

  function isBrowserIOSInAppLinkedin(): boolean {
    return isDeviceIOS() && _matchesUserAgent(/LinkedInApp/);
  }

  function isBrowserIOSInAppInstagram(): boolean {
    return isDeviceIOS() && !!window.document.referrer.match("//l.instagram.com/");
  }

  function isBrowserIOSInAppThreads(): boolean {
    return isBrowserIOSInAppInstagram();
  }

  function isBrowserIOSInAppTwitter(): boolean {
    return isDeviceIOS() && !!window.document.referrer.match("//t.co/");
  }

  function isBrowserAndroidChrome(): boolean {
    return (
      isDeviceAndroid() &&
      !!_matchesUserAgent(/Chrome/) &&
      !isBrowserAndroidFacebook() &&
      !isBrowserAndroidSamsung() &&
      !isBrowserAndroidFirefox() &&
      !isBrowserAndroidEdge()
    );
  }

  function isBrowserAndroidEdge(): boolean {
    return isDeviceAndroid() && _matchesUserAgent(/Edge/);
  }

  function isBrowserAndroidFirefox(): boolean {
    return isDeviceAndroid() && _matchesUserAgent(/Firefox/);
  }

  function isBrowserAndroidFacebook(): boolean {
    return isDeviceAndroid() && _matchesUserAgent(/FBAN|FBAV/);
  }

  function isBrowserAndroidSamsung(): boolean {
    return isDeviceAndroid() && _matchesUserAgent(/SamsungBrowser/);
  }

  function isDesktopChrome(): boolean {
    return _matchesUserAgent(/Chrome/) && !_matchesUserAgent(/Mobile/) && !isDesktopEdge();
  }

  function isDesktopEdge(): boolean {
    return _matchesUserAgent(/Edge/) && !_matchesUserAgent(/Mobile/);
  }

  function isDesktopSafari(): boolean {
    return _matchesUserAgent(/Safari/) && !_matchesUserAgent(/Chrome/) && !_matchesUserAgent(/Mobile/);
  }

  function isDesktopWindows(): boolean {
    return _matchesUserAgent(/Windows/);
  }

  function isDesktopMac(): boolean {
    return _matchesUserAgent(/Macintosh/);
  }

  function isStandAlone(): boolean {
    return (
      !!("standalone" in window.navigator && (window.navigator as any).standalone) || 
      !!window.matchMedia("(display-mode: standalone)").matches
    );
  }

  function getModalDisplayCount(): number {
    const count = localStorage.getItem(`adhs-modal-display-count-${window.location.hostname}`);
    return count ? parseInt(count, 10) : 0;
  }

  function setModalDisplayCount(count: number): void {
    localStorage.setItem(`adhs-modal-display-count-${window.location.hostname}`, count.toString());
  }

  function hasReachedMaxModalDisplayCount(): boolean {
    if (maxModalDisplayCount === -1) return false;
    return getModalDisplayCount() >= maxModalDisplayCount;
  }

  function incrModalDisplayCount(): void {
    setModalDisplayCount(getModalDisplayCount() + 1);
  }

  function clearModalDisplayCount(): void {
    setModalDisplayCount(0);
  }

  // Desktop install prompt handling
  function registerDesktopInstallPromptEvent(): void {
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      desktopInstallPromptEvent = e;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
  }

  function shouldShowDesktopInstallPromptBasedOnDevice(): boolean {
    return isDesktopChrome() || isDesktopEdge();
  }

  function getCachedInstallPrompt(): BeforeInstallPromptEvent | null {
    return desktopInstallPromptEvent;
  }

  async function triggerInstallPrompt(): Promise<void> {
    if (desktopInstallPromptEvent) {
      try {
        await desktopInstallPromptEvent.prompt();
        const choiceResult = await desktopInstallPromptEvent.userChoice;
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the install prompt');
          localStorage.setItem('snugglecrafters_app_installed', 'true');
        }
        desktopInstallPromptEvent = null;
      } catch (error) {
        console.error('Error showing install prompt:', error);
      }
    }
  }

  // Modal management
  function closeModal(): void {
    const container = document.querySelector('.adhs-container');
    if (container) {
      container.classList.remove('visible');
      setTimeout(() => {
        container.remove();
        modalIsShowingState = false;
        if (closeEventListener) {
          window.removeEventListener('touchstart', closeEventListener);
          window.removeEventListener('click', closeEventListener);
          closeEventListener = null;
        }
      }, isDeviceIOS() ? 500 : 300);
    }
  }

  function modalIsShowing(): boolean {
    return modalIsShowingState;
  }

  function show(locale?: string): DeviceInfo {
    // Set up locale
    if (locale && !localeCatalog[locale]) {
      console.log("add-to-homescreen: WARNING: locale selected not available:", locale);
      locale = "";
    }

    if (!locale) {
      const languageFromBrowser = i18n._getLanguageFromBrowserSettings();
      if (languageFromBrowser && localeCatalog[languageFromBrowser]) {
        locale = languageFromBrowser;
      } else if (localeCatalog["en"]) {
        locale = "en";
      } else {
        locale = Object.keys(localeCatalog)[0];
      }
    }

    i18n.setLocale(locale || 'en');

    let device: DeviceType;
    if (isDeviceIOS()) {
      device = DeviceType.IOS;
    } else if (isDeviceAndroid()) {
      device = DeviceType.ANDROID;
    } else {
      device = DeviceType.DESKTOP;
    }

    if (isStandAlone()) {
      return new DeviceInfo(true, true, device);
    }

    // Determine if device can be standalone without auto-showing modal
    let canBeStandAlone = false;
    if (isDeviceIOS()) {
      canBeStandAlone = isBrowserIOSSafari() || isBrowserIOSChrome();
    } else if (isDeviceAndroid()) {
      canBeStandAlone = isBrowserAndroidChrome() || isBrowserAndroidEdge() || isBrowserAndroidFirefox();
    } else if (isDesktopChrome() || isDesktopEdge() || isDesktopSafari()) {
      canBeStandAlone = true;
    }

    return new DeviceInfo(false, canBeStandAlone, device);
  }

  function showModal(locale?: string): DeviceInfo {
    const deviceInfo = show(locale);
    
    if (hasReachedMaxModalDisplayCount() || !deviceInfo.canBeStandAlone) {
      return deviceInfo;
    }

    // Show modal for mobile devices
    if (displayOptions.showMobile && (isDeviceIOS() || isDeviceAndroid())) {
      if (deviceInfo.canBeStandAlone) {
        incrModalDisplayCount();
        modalIsShowingState = true;
      }
      return deviceInfo;
    }

    // Show modal for desktop - DON'T auto-trigger install prompt
    if (displayOptions.showDesktop && (isDesktopChrome() || isDesktopEdge() || isDesktopSafari())) {
      incrModalDisplayCount();
      modalIsShowingState = true;
      // Cache the install prompt but don't auto-trigger it
      return deviceInfo;
    }

    return deviceInfo;
  }

  // Initialize desktop install prompt handling
  if (shouldShowDesktopInstallPromptBasedOnDevice()) {
    registerDesktopInstallPromptEvent();
  }

  return {
    appName,
    appIconUrl,
    assetUrl,
    maxModalDisplayCount,
    displayOptions,
    allowClose: allowClose !== false,
    showArrow: showArrow !== false,
    clearModalDisplayCount,
    isStandAlone,
    show,
    showModal,
    closeModal,
    modalIsShowing,
    hasReachedMaxModalDisplayCount,
    getCachedInstallPrompt,
    triggerInstallPrompt,
    isDeviceIOS,
    isDeviceAndroid,
    isBrowserIOSSafari,
    isBrowserIOSChrome,
    isBrowserIOSFirefox,
    isBrowserIOSInAppFacebook,
    isBrowserIOSInAppLinkedin,
    isBrowserIOSInAppInstagram,
    isBrowserIOSInAppThreads,
    isBrowserIOSInAppTwitter,
    isBrowserAndroidChrome,
    isBrowserAndroidFacebook,
    isBrowserAndroidSamsung,
    isBrowserAndroidFirefox,
    isDesktopWindows,
    isDesktopMac,
    isDesktopChrome,
    isDesktopSafari,
    isDesktopEdge
  };
}